import { addYears, endOfYear } from 'date-fns'
import format from 'date-fns/format'
import objectHash from 'object-hash'
import React, { useEffect, useMemo, useState } from 'react'
import { useInterval } from 'react-use'
import styled from 'styled-components'
import {
  buildCompleteTimeSeriesPreviewSeries,
  buildCompleteTimeSeriesPreviewTime,
} from '~/components/timeSeriesPreview.util'
import useAction from '~/prix/react/hooks/action'
import { parseDate, stringifyDate } from '~/prix/types/date'
import { formatDateTime } from '~/prix/types/dateTime'
import {
  generateIncidenceDates,
  timeTypeForCurrentOnly,
  timeTypeForIncidenceRealtime,
  timeTypeForIncidenceRealtimePrediction,
  timeTypeForPrevalence,
} from '~/prix/types/presets/time'
import actionDefinitions from '../actionDefinitions'
import {
  Layer,
  SpaceGeoStandardId,
  TimeIncidence,
  TimePrevalence,
} from '~/packages/general/variable/spaceTime.types'
import Modal from '~/prix/react/components/modal'
import Handle from '~/prix/react/components/handle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import TimeSeriesPreview from '~/components/timeSeriesPreview'
import { ButtonLink, ButtonLinkAsSimpleText } from '~/prix/react/components/button'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import { ReportWithLayer } from './reports'
import DateField from '~/prix/react/components/form/field/dateTimeField'
import { buildOptionalStatsActionInput } from './statsActionOptionalInput.util'
import { isVariableLayer } from './clusterListInput.util'
import useAPI from '~/prix/react/hooks/api'
import {
  buildStatsSpaceForUserScope,
  getPreferredSpaceFromGeneralSettings,
} from './userDelimitationScope.util'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 80vh;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    max-height: 70vh;
  }

  .top {
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 1rem;

    input {
      border: none;
      border-radius: 8px;
      padding: 1rem;
    }

    .span {
      margin: 0 1rem;

      @media (max-width: 575px) {
        display: none;
      }
    }

    .date-input-wrapper {
      display: flex;
      flex-direction: row;
      padding-top: 12px;
      width: fit-content;
      gap: 12px;
      justify-content: center;
      align-items: center;

      .title {
        @media (min-width: 575px) {
          display: none;
        }
        @media (max-width: 575px) {
          margin: 0 3px 2px 0;
        }
      }

      @media (max-width: 575px) {
        flex: 1;
        gap: unset;
        flex-direction: column;
        align-items: flex-start;

        input {
          margin-bottom: 7px;
        }
      }
    }
  }

  .bottom {
    display: flex;
    position: relative;
    width: 100%;
    height: clamp(190px, 28vh, 250px);
    min-height: 190px;
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    padding: 8px 14px 10px;

    @media (max-width: 575px) {
      height: clamp(170px, 25vh, 210px);
      min-height: 170px;
      padding: 8px 10px 10px;
    }
  }

  .children-options {
    flex: 1;
    overflow-x: hidden;

    overflow-y: scroll;

    .children-option {
      display: flex;
      position: relative;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      cursor: pointer;

      &:not(:last-child)::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        margin-left: 1rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
        width: 100%;
      }
      .active {
        background-color: ${({ theme }) => theme.colors.lightGrey};
        cursor: not-allowed;
      }
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  padding: 10px;
  justify-content: right;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`
const LastButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    margin-bottom: 10px;
  }
`

const ArticleItems = styled.article`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    display: block;
    margin-left: 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
    width: 100%;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightGrey};

    &:last-child {
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }

  .title {
    display: flex;
    align-items: center;
    font-weight: bold;
    gap: 4px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    flex-direction: column;
    align-items: flex-start;

    .right {
      margin-top: 1rem;
      justify-content: flex-start;
    }
  }
`

const AlertWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  gap: 1rem;
`

interface ViewSelectModalTimeProps {
  layer: Layer
  isOpen: boolean
  onClose: () => void
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  onChangeReport: (report: ReportWithLayer | null) => void
}

export default function ViewSelectModalTime({
  layer,
  isOpen,
  onClose,
  onChangeLayer,
  onChangeReport,
}: ViewSelectModalTimeProps) {
  if (!isVariableLayer(layer)) {
    return <output>Seleção temporal indisponível para essa camada.</output>
  }

  const [now, setNow] = useState(() => formatDateTime(new Date()))
  const [currentLayer, setCurrentLayer] = useState<typeof layer>(layer)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const maxSelectableDate = useMemo(() => endOfYear(addYears(new Date(), 1)), [])
  const { context } = useAPI()
  const preferredGeneralSettings = context.user?.activeClient?.settings?.general
  const preferredSpace = useMemo(
    () => getPreferredSpaceFromGeneralSettings(preferredGeneralSettings),
    [
      preferredGeneralSettings?.preferredSpaceChildrenType,
      preferredGeneralSettings?.preferredSpaceDelimitationId,
      preferredGeneralSettings?.preferredSpaceDelimitationTitle,
      preferredGeneralSettings?.preferredSpaceDelimitationType,
    ],
  )

  useEffect(() => {
    setCurrentLayer(layer)
  }, [layer])

  const previewTimeType = currentLayer.variable.timeType
  const previewIsPredicted =
    previewTimeType === 'incidence' && (currentLayer.time as TimeIncidence).type.endsWith('Predicted')

  const previewTime = useMemo(() => {
    if (previewTimeType !== 'incidence') {
      return null
    }

    return buildCompleteTimeSeriesPreviewTime({
      isPredicted: previewIsPredicted,
    })
  }, [previewIsPredicted, previewTimeType])

  const previewActionDefinition = useMemo(() => {
    const actionDefinition = actionDefinitions.find(
      actionDefinition => actionDefinition.key === currentLayer.variable.actionDefinitionKey,
    )
    if (!actionDefinition) {
      throw new Error(`Action definition not found for variable ${currentLayer.variable.key}`)
    }

    return actionDefinition
  }, [currentLayer.variable.actionDefinitionKey, currentLayer.variable.key])

  const previewSpaceDelimitationId = (
    currentLayer.space as {
      delimitationId: SpaceGeoStandardId
    }
  ).delimitationId

  const previewSpace = useMemo(
    () =>
      buildStatsSpaceForUserScope(
        {
          delimitationType: currentLayer.space.delimitationType,
          delimitationId: previewSpaceDelimitationId,
        },
        preferredSpace,
      ),
    [
      currentLayer.space.delimitationType,
      previewSpaceDelimitationId,
      preferredSpace?.delimitationId,
      preferredSpace?.delimitationType,
    ],
  )

  const previewTimeInput = useMemo(
    () =>
      previewTime ?? {
        ...currentLayer.time,
        groupType: 'daily' as const,
      },
    [currentLayer.time, previewTime],
  )

  const previewInput = useMemo(() => {
    const input = {
      variable: currentLayer.variable,
      ...buildOptionalStatsActionInput({
        filter: currentLayer.variable.filter,
        highlight: currentLayer.variable.highlight,
        space: previewSpace,
        time: previewTimeInput,
      }),
      analysis:
        previewIsPredicted && currentLayer.variable.timeSeries
          ? {
              timeSeries: currentLayer.variable.timeSeries,
              include: {
                items: true,
                prediction: 'auto' as const,
                alerts: false,
                insights: false,
                components: false,
              },
            }
          : undefined,
    }
    return input
  }, [currentLayer.variable, previewIsPredicted, previewSpace, previewTimeInput])

  const previewFilterHash = useMemo(
    () =>
      objectHash(currentLayer.variable.filter ?? null, {
        respectType: false,
      }),
    [currentLayer.variable.filter],
  )

  const previewHighlightHash = useMemo(
    () =>
      objectHash(currentLayer.variable.highlight ?? null, {
        respectType: false,
      }),
    [currentLayer.variable.highlight],
  )

  // The preview request should only rerun when its semantic payload changes.
  const previewRequestKey = useMemo(
    () =>
      objectHash(
        {
          actionDefinitionKey: previewActionDefinition.key,
          variableKey: currentLayer.variable.key,
          filter: previewFilterHash,
          highlight: previewHighlightHash,
          space: previewSpace,
          time: previewTimeInput,
          prediction: previewIsPredicted,
        },
        {
          respectType: false,
        },
      ),
    [
      currentLayer.variable.key,
      previewActionDefinition.key,
      previewFilterHash,
      previewHighlightHash,
      previewIsPredicted,
      previewSpace,
      previewTimeInput,
    ],
  )

  const { timeType } = currentLayer.variable

  const previewResult = useAction(
    previewActionDefinition,
    previewInput,
    [previewRequestKey],
    {
      autoLoad: isOpen && currentLayer.variable.timeType === 'incidence' && Boolean(previewTime),
      staleWhileRevalidate: true,
    },
  )

  const { previewValues, previewHighlights, previewPredictions, previewDates } = useMemo(() => {
    if (!previewTime || !previewResult.result?.items) {
      return {
        previewValues: [] as number[],
        previewHighlights: [] as boolean[],
        previewPredictions: [] as boolean[],
        previewDates: [] as string[],
      }
    }

    const previewSeries = buildCompleteTimeSeriesPreviewSeries(previewResult.result.items as any[], {
      endDate: previewTime.endDate!,
    })

    return {
      previewValues: previewSeries.values,
      previewHighlights: previewSeries.highlights,
      previewPredictions: previewSeries.predictions,
      previewDates: previewSeries.dates,
    }
  }, [previewResult.result, previewTime])

  const isPreviewLoading = previewResult.isLoading

  useInterval(
    () => {
      setNow(formatDateTime(new Date()))
    },
    isOpen && timeType === 'currentOnly' ? 100 : null,
  )

  useEffect(() => {
    if (timeType !== 'incidence') {
      setStartDate(null)
      setEndDate(null)
      return
    }

    setStartDate(
      (currentLayer.time as TimeIncidence).startDate
        ? (currentLayer.time as TimeIncidence).startDate!
        : null,
    )
    setEndDate(
      (currentLayer.time as TimeIncidence).endDate
        ? (currentLayer.time as TimeIncidence).endDate!
        : null,
    )

    return
  }, [timeType, currentLayer])

  const shapeByTimeType: Record<typeof timeType, Record<string, string>> = {
    prevalence: timeTypeForPrevalence.keysAndLabels ?? {},
    incidence: {
      ...timeTypeForIncidenceRealtime.keysAndLabels,
      ...timeTypeForIncidenceRealtimePrediction.keysAndLabels,
    },
    currentOnly: timeTypeForCurrentOnly.keysAndLabels ?? {},
  }

  const shape = shapeByTimeType[timeType]

  const entries = Object.entries(shape)

  const handleSelectChildrenOption = (key: string) => {
    if (currentLayer.time.type === key) return

    if (timeType === 'currentOnly') {
      return
    }
    if (timeType === 'prevalence') {
      setCurrentLayer(oldLayer => {
        const date = new Date()
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            type: key as TimePrevalence['type'],
            title: shape[key],
            date,
          },
        }
        return newLayer
      })

      return
    }

    const [newStartDateObj, newEndDateObj] =
      generateIncidenceDates[key as keyof typeof generateIncidenceDates]()

    const [newStartDate, newEndDate] = [
      stringifyDate(newStartDateObj),
      stringifyDate(newEndDateObj),
    ]

    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          startDate: newStartDate,
          endDate: newEndDate,
          title: shape[key],
          type: key as TimeIncidence['type'],
        },
      }
      return newLayer
    })

    setStartDate(newStartDate)
    setEndDate(newEndDate)
  }

  const handleSelectStartDate = (startDateString: string | null) => {
    if (timeType !== 'incidence') {
      console.warn('handleSelectEndDate called with timeType', timeType)
      return
    }

    if (!startDateString) {
      setStartDate(null)
      setCurrentLayer(oldLayer => {
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            title: 'Intervalo de datas personalizado',
            type: 'customDateRange',
            startDate: undefined,
            endDate: (oldLayer.time as TimeIncidence).endDate,
          },
        }
        return newLayer
      })
      return
    }

    setStartDate(startDateString)
    const parsedStartDate = startDateString

    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...oldLayer.time,
          title:
            (oldLayer.time as TimeIncidence).endDate! <= stringifyDate(new Date())
              ? 'Intervalo de datas personalizado'
              : 'Intervalo de datas personalizado (predição)',
          type:
            (oldLayer.time as TimeIncidence).endDate! <= stringifyDate(new Date())
              ? 'customDateRange'
              : 'customDateRangePredicted',
          startDate: parsedStartDate,
          endDate: (oldLayer.time as TimeIncidence).endDate,
        },
      }
      return newLayer
    })
  }

  const handleSelectEndDate = (endDateString: string | null) => {
    if (timeType !== 'incidence') {
      console.warn('handleSelectEndDate called with timeType', timeType)
      return
    }

    if (!endDateString) {
      setEndDate(null)
      setCurrentLayer(oldLayer => {
        const newLayer: Layer = {
          ...oldLayer,
          time: {
            ...oldLayer.time,
            title: 'Intervalo de datas personalizado',
            type: 'customDateRange',
            startDate: (oldLayer.time as TimeIncidence).startDate,
            endDate: undefined,
          },
        }
        return newLayer
      })
      return
    }

    setEndDate(endDateString)
    const endDate = endDateString
    setCurrentLayer(oldLayer => {
      const newLayer: Layer = {
        ...oldLayer,
        time: {
          ...oldLayer.time,
          title:
            endDate <= stringifyDate(new Date())
              ? 'Intervalo de datas personalizado'
              : 'Intervalo de datas personalizado (predição)',
          type:
            endDate <= stringifyDate(new Date()) ? 'customDateRange' : 'customDateRangePredicted',
          startDate: (oldLayer.time as TimeIncidence).startDate,
          endDate: endDate,
        },
      }
      return newLayer
    })
  }

  const handleCancel = () => {
    setCurrentLayer(layer)
    onClose()
  }
  const handleAnalyze = () => {
    onChangeReport({
      layer: currentLayer,
      reportKey: 'timeAnalysis',
      reportPayload: {
        spaceDelimitationType: currentLayer.space.delimitationType,
        spaceDelimitationId: (
          currentLayer.space as {
            delimitationId: SpaceGeoStandardId
          }
        ).delimitationId,
      },
    })
    onClose()
  }
  const handleConfirm = () => {
    onChangeLayer(currentLayer)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Handle>
        <Wrapper>
          <div className='top'>
            {timeType === 'currentOnly' && now}
            {timeType === 'incidence' && (
              <div className='date-input-wrapper'>
                <span className='title'>Início</span>
                <DateField
                  id='start-day-input'
                  value={startDate}
                  placeholder='De'
                  onDirectChange={date =>
                    handleSelectStartDate(date ? format(date, 'yyyy-MM-dd') : null)
                  }
                  maxDate={maxSelectableDate}
                  isRequired
                />
                <span className='span'>até</span>
                <span className='title'>Fim</span>
                <DateField
                  id='final-day-input'
                  value={endDate}
                  placeholder='Até'
                  onDirectChange={date =>
                    handleSelectEndDate(date ? format(date, 'yyyy-MM-dd') : null)
                  }
                  maxDate={maxSelectableDate}
                  isRequired
                />
              </div>
            )}
          </div>
          <div className='children-options'>
            {entries.map(([key, name], index) => {
              return (
                <ArticleItems
                  key={index}
                  className={`children-option ${currentLayer.time.type === key ? 'active' : ''}`}
                  role='button'
                  onClick={isPreviewLoading ? () => {} : () => handleSelectChildrenOption(key)}
                  style={{ cursor: isPreviewLoading ? 'wait' : 'pointer' }}
                >
                  <div className='title'>
                    {currentLayer.time.type === key && (
                      <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#0C2340' }} />
                    )}
                    {name}
                  </div>
                </ArticleItems>
              )
            })}
          </div>
          {timeType === 'incidence' &&
            (!startDate || !endDate ? (
              <AlertWrapper>
                <AlertIcon fill={'#f1c40f'} width={18} height={18} />
                <p>{`A data ${!startDate ? 'inicial' : 'final'} não pode estar vazia`}</p>
              </AlertWrapper>
            ) : parseDate(startDate) > parseDate(endDate) ? (
              <AlertWrapper>
                <AlertIcon fill={'#f1c40f'} width={18} height={18} />
                <p>A data inicial não pode ser maior que a data final</p>
              </AlertWrapper>
            ) : previewValues.length > 0 || isPreviewLoading ? (
              <div className='bottom'>
                <TimeSeriesPreview
                  isLoading={isPreviewLoading}
                  values={previewValues}
                  dates={previewDates as string[]}
                  highlights={previewHighlights}
                  predictions={previewPredictions}
                  selectedRange={{
                    startDate,
                    endDate,
                  }}
                />
              </div>
            ) : null)}
          <ButtonContainer>
            <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleCancel}>
              Cancelar
            </ButtonLinkAsSimpleText>
            {timeType === 'incidence' && (
              <ButtonLinkAsSimpleText style={{ marginRight: '7px' }} onGo={handleAnalyze}>
                Analisar tempo
              </ButtonLinkAsSimpleText>
            )}
            <LastButton>
              <ButtonLink onGo={handleConfirm}>Confirmar</ButtonLink>
            </LastButton>
          </ButtonContainer>
        </Wrapper>
      </Handle>
    </Modal>
  )
}
